import styled from "styled-components";
import ButtonIcon from "../../ui/ButtonIcon";
import LeadsButton from "../../ui/LeadsButton";
import { useNavigate, useParams } from "react-router-dom";
import { PiMagicWandDuotone } from "react-icons/pi";
import { BiMessageAlt } from "react-icons/bi";
import { useGetContractAttachments } from "./useGetContractAttachments";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ThemedSkeleton from "../../ui/ThemedSkeleton";
import { PermissionClaim } from "supertokens-auth-react/recipe/userroles";
import Session from "supertokens-auth-react/recipe/session";
import { ExternalLinkIcon, Eye, Route } from "lucide-react";
import { useUiStore } from "@/stores/UiStore";
import ManageBillingButton from "../settings/ManageBillingButton";
import { useCreatePursuit } from "../pursuit/useCreatePursuit";

import { toPascalCase } from "@/utils/helpers";
import { useCreateBlockNote } from "@blocknote/react";
import { extractGrantData } from "./GrantContract";
const Operations = styled.div`
  display: flex;
  justify-content: right;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

function getContacts(contract, searchType) {
  var contacts = [];
  switch (searchType) {
    case "federal":
      if (contract?.PrimaryContactFullname)
        contacts.push({
          name: contract?.PrimaryContactFullname,
          email: contract?.PrimaryContactEmail,
          phone: contract?.PrimaryContactPhone,
          role: "Primary",
        });
      if (contract?.SecondaryContactFullname)
        contacts.push({
          name: contract?.SecondaryContactFullname,
          email: contract?.SecondaryContactEmail,
          phone: contract?.SecondaryContactPhone,
          role: "Secondary",
        });
      return contacts;
    case "sled":
      if (contract?.contactInfo?.length > 0) {
        contract.contactInfo.forEach((contact) => {
          contacts.push({
            name: contact?.name,
            email: contact?.email,
            phone: contact?.phone,
          });
        });
      }
      return contacts;
    case "grant":
      const { forecast = null, synopsis = null } = contract || {};
      const { agencyContactName, agencyContactEmail, agencyContactPhone } =
        synopsis ? extractGrantData(synopsis) : extractGrantData(forecast);
      if (agencyContactName) {
        contacts.push({
          name: agencyContactName,
          email: agencyContactEmail,
          phone: agencyContactPhone,
        });
      }
      return contacts;
    default:
      return contacts;
  }
}
function CreatePursuitButton({
  renderButton,
  isLoading,
  status,
  contract,
  isNavigateOnSuccess,
}) {
  const { searchType, proposal_id } = useParams();
  const title = toPascalCase(
    contract?.Title || contract?.title || contract?.opportunityTitle
  );
  const description = contract?.Summary || contract?.summary;
  const dueDate =
    contract?.ResponseDeadLine ||
    contract?.dueDate ||
    contract?.responseDate ||
    contract?.closeDate;

  const contacts = getContacts(contract, searchType);
  const { mutate, isLoading: isCreatePursuitLoading } = useCreatePursuit(
    searchType,
    proposal_id,
    isNavigateOnSuccess
  );
  // Creates a new editor instance.
  const editor = useCreateBlockNote({});
  async function createPursuit() {
    const blocks = await editor.tryParseHTMLToBlocks(description);
    mutate({
      title,
      search_type: searchType,
      proposal_id,
      description: { blocks: blocks },
      due_date: dueDate,
      status,
      contacts,
    });
  }

  return renderButton({
    createPursuit,
    isLoading,
    isCreatePursuitLoading,
  });
}

function ContractOperation({ contract, isLoading }) {
  let disabledDueToNoAttachments = false;
  const claimValue = Session.useClaimValue(PermissionClaim);
  const permissions = claimValue.value;

  const navigate = useNavigate();
  const { searchType, proposal_id } = useParams();

  const { data: attachments = [], isLoading: isAttachmentsLoading } =
    useGetContractAttachments(searchType, proposal_id);

  let disabledDueToNoFederalAttachments =
    isAttachmentsLoading || !attachments?.length;

  if (searchType === "federal" && attachments?.length) {
    const allAttachmentsAreLinks = attachments.every(
      (att) => att.type === "link"
    );
    if (allAttachmentsAreLinks) {
      disabledDueToNoFederalAttachments = true;
    }
  }
  if (searchType === "federal" && disabledDueToNoFederalAttachments) {
    disabledDueToNoAttachments = true;
  } else if (
    contract &&
    searchType === "sled" &&
    contract["attachments"]?.length === 0
  ) {
    disabledDueToNoAttachments = true;
  } else if (
    contract &&
    searchType === "grant" &&
    contract["synopsisAttachmentFolders"].length === 0
  ) {
    disabledDueToNoAttachments = true;
  }

  const toggleChat = async () => {
    // check if assistant already created for this contract and user
    useUiStore.setState((state) => ({
      wideLayout: !state.wideLayout,
      sidebarExpanded: false,
    }));
  };

  const typeInfo = {
    federal: {
      link: contract?.Link,
      contractId: contract?.Sol,
    },
    sled: {
      link: contract?.originalSource,
      contractId: contract?.solicitationId,
    },
    grant: {
      link: `https://www.grants.gov/search-results-detail/${contract?.id}`,
      contractId: contract?.opportunityNumber,
    },

    sbir: {
      link: contract?.sbirTopicLink,
      contractId: contract?.topicNumber,
    },
  };

  const disableProposalGen =
    isLoading ||
    !(Array.isArray(permissions) && permissions.includes("proposal")) ||
    searchType === "sbir" ||
    disabledDueToNoAttachments === true;

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="row-start-1 col-span-3 text-muted-foreground text-sm">
          {"ID: "} {typeInfo[searchType].contractId}
        </span>
        <Operations>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger>
                <LeadsButton
                  variant="outline"
                  disabled={isLoading}
                  id="contract-save-button"
                  className="h-11"
                  proposalId={proposal_id}
                  searchType={searchType}
                />
              </TooltipTrigger>
              <TooltipContent>Save opportunity</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger>
                <ButtonIcon
                  id="contract-view-button"
                  variant="outline"
                  className="[&_svg]:size-4 h-11"
                  disabled={isLoading}
                  onClick={() =>
                    window.open(
                      typeInfo[searchType].link,
                      "_blank",
                      "noreferrer"
                    )
                  }
                >
                  <ExternalLinkIcon className="text-secondary-foreground" />
                  View
                </ButtonIcon>
              </TooltipTrigger>
              <TooltipContent>
                <p>View original opportunity</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger>
                <CreatePursuitButton
                  isLoading={isLoading}
                  contract={contract}
                  status="in_progress"
                  isNavigateOnSuccess
                  renderButton={({
                    createPursuit,
                    isLoading,
                    isCreatePursuitLoading,
                  }) => (
                    <ButtonIcon
                      id="contract-generate-button"
                      disabled={
                        disableProposalGen ||
                        isLoading ||
                        isCreatePursuitLoading
                      }
                      className="w-47 gap-2 bg-primary"
                      type="button"
                      onClick={() => {
                        if (contract?.pursuit_id) {
                          navigate(
                            `/journey-hub/${contract?.pursuit_id}/proposal`
                          );
                        } else {
                          createPursuit();
                        }
                      }}
                    >
                      <PiMagicWandDuotone />
                      {isCreatePursuitLoading
                        ? "Starting Journey..."
                        : "Generate Proposal"}
                    </ButtonIcon>
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {Array.isArray(permissions) &&
                  permissions.includes("proposal") ? (
                    disabledDueToNoAttachments === true ? (
                      "Not enough information to generate a proposal"
                    ) : (
                      "Generate a tailored proposal for this opportunity using AI"
                    )
                  ) : (
                    <ManageBillingButton
                      variant="link"
                      text="Upgrade to generate proposals"
                      className="text-sm p-0 h-full"
                    />
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger>
                <ButtonIcon
                  id="contract-chat-button"
                  disabled={isLoading}
                  className="w-47 gap-2 bg-primary"
                  onClick={toggleChat}
                >
                  <BiMessageAlt />
                  Ask SamSearch AI
                </ButtonIcon>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Ask for details about this opportunity and its attachments
                </p>
              </TooltipContent>
            </Tooltip>
            {contract?.pursuit_id ? (
              <ButtonIcon
                id="contract-chat-button"
                disabled={isLoading}
                className="w-47 gap-2 bg-primary"
                onClick={() => navigate(`/journey-hub/${contract?.pursuit_id}`)}
              >
                <Eye />
                View Journey
              </ButtonIcon>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CreatePursuitButton
                    isLoading={isLoading}
                    contract={contract}
                    renderButton={({
                      createPursuit,
                      isLoading,
                      isCreatePursuitLoading,
                    }) => (
                      <ButtonIcon
                        id="contract-chat-button"
                        disabled={isLoading || isCreatePursuitLoading}
                        className="w-47 gap-2 bg-primary"
                        onClick={createPursuit}
                      >
                        <Route />
                        {isCreatePursuitLoading
                          ? "Starting Journey..."
                          : "Start Journey"}
                      </ButtonIcon>
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Start Journey</p>
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </Operations>
      </div>

      <div
        id="contract-summary"
        className="bg-secondary/40  p-4 rounded-md leading-7"
      >
        {isLoading ? (
          <>
            <ThemedSkeleton className="bg-muted w-11/12" />
            <ThemedSkeleton className="bg-muted h-2 w-9/12" />
            <ThemedSkeleton className="bg-muted w-8/12" />
            <ThemedSkeleton className="bg-muted w-10/12" />
          </>
        ) : (
          <>
            <div className="text-muted-foreground mb-1">Smart Summary</div>
            {contract?.Summary || contract?.summary}
          </>
        )}
      </div>
    </>
  );
}

export default ContractOperation;
